import { Card, CardContent } from "@/components/ui/card";
import {
  TrendingUp,
  CalendarRange,
  Users,
  Coins,
  AlertTriangle,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { AdminSubscriptionAnalytics } from "@/types/subscription";
import {
  countActivePaidSubscribers,
  formatKrw,
  formatKrwCompact,
} from "./subscription-analytics-utils";

interface SubscriptionAnalyticsKpiCardsProps {
  analytics: AdminSubscriptionAnalytics;
}

interface KpiTone {
  iconBg: string;
  iconColor: string;
}

const TONE_EMERALD: KpiTone = {
  iconBg: "bg-emerald-50",
  iconColor: "text-emerald-600",
};
const TONE_BLUE: KpiTone = {
  iconBg: "bg-blue-50",
  iconColor: "text-blue-600",
};
const TONE_INDIGO: KpiTone = {
  iconBg: "bg-indigo-50",
  iconColor: "text-indigo-600",
};
const TONE_VIOLET: KpiTone = {
  iconBg: "bg-violet-50",
  iconColor: "text-violet-600",
};
const TONE_AMBER: KpiTone = {
  iconBg: "bg-amber-50",
  iconColor: "text-amber-600",
};

interface KpiCardProps {
  label: string;
  primary: string;
  secondary: string;
  icon: LucideIcon;
  tone: KpiTone;
}

function KpiCard({
  label,
  primary,
  secondary,
  icon: Icon,
  tone,
}: KpiCardProps) {
  return (
    <Card className="pt-2 pb-2">
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center gap-2">
          <div className={cn("rounded-md p-1.5", tone.iconBg)}>
            <Icon className={cn("h-4 w-4", tone.iconColor)} />
          </div>
          <span className="text-base font-semibold tracking-tight">
            {label}
          </span>
        </div>
        <div className="mt-3 text-3xl font-bold tabular-nums tracking-tight">
          {primary}
        </div>
        <p className="mt-1 text-sm text-muted-foreground tabular-nums">
          {secondary}
        </p>
      </CardContent>
    </Card>
  );
}

export function SubscriptionAnalyticsKpiCards({
  analytics,
}: SubscriptionAnalyticsKpiCardsProps) {
  const activePaidCount = countActivePaidSubscribers(analytics.breakdown);
  const arpu = activePaidCount > 0 ? analytics.mrrKrw / activePaidCount : 0;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
      <KpiCard
        label="MRR"
        primary={formatKrwCompact(analytics.mrrKrw)}
        secondary={formatKrw(analytics.mrrKrw)}
        icon={TrendingUp}
        tone={TONE_EMERALD}
      />
      <KpiCard
        label="ARR"
        primary={formatKrwCompact(analytics.arrKrw)}
        secondary="MRR × 12"
        icon={CalendarRange}
        tone={TONE_BLUE}
      />
      <KpiCard
        label="활성 유료 구독자"
        primary={activePaidCount.toLocaleString()}
        secondary="PLUS / PRO / PREMIUM"
        icon={Users}
        tone={TONE_INDIGO}
      />
      <KpiCard
        label="ARPU"
        primary={formatKrwCompact(arpu)}
        secondary="MRR ÷ 활성 유료"
        icon={Coins}
        tone={TONE_VIOLET}
      />
      <KpiCard
        label="해지 대기"
        primary={analytics.pendingCancellationCount.toLocaleString()}
        secondary="PENDING_CANCELLATION"
        icon={AlertTriangle}
        tone={TONE_AMBER}
      />
    </div>
  );
}
